/**
 * Design tokens for the Fundraising row.
 *
 * Written after a consistency pass found the row using two different primary
 * button colours with no rule behind which was which (2026-08-16). The colours
 * were not the real problem — the absence of a rule was, because every new
 * panel then guessed, and the guesses diverged.
 *
 * The rule, and the reason it is worth having:
 *
 *   GENERATE (orange) — Lev is about to do work: research a category, draft a
 *     deck, score an upload, turn gaps into questions. Orange because that is
 *     what it means everywhere else in Lev, not just here.
 *   COMMIT (slate) — the founder is doing the work: saving, moving to the next
 *     step, copying something out. Neutral because it is not Lev's action.
 *   SECONDARY — a real alternative to the primary, not a lesser version of it.
 *   QUIET — an escape or an aside (skip, back, hide). Deliberately unstyled so
 *     it never competes with the two above.
 *
 * One accent per screen is the constraint that makes it work. Two orange
 * buttons side by side means neither reads as the action.
 *
 * Enforced by `src/tests/design.fundraisingConsistency.test.ts`, which fails
 * on raw colour classes in these panels. Change a token here and the whole row
 * follows; that single-lever property is the point of the file.
 *
 * Cross-compatible module: no "use client", no server-only imports.
 */

/*
  ── Buttons never wrap ──────────────────────────────────────────────────────

  Every token below carries `whitespace-nowrap`, and that is not cosmetic.

  These buttons sit in flex rows beside a sentence of explanation. When the
  panel narrows, the SENTENCE should reflow and the button should not: a label
  broken across two lines stops reading as one control, and it changes the
  button's height, which drags the whole row out of alignment with everything
  above it. The founder's report was the button at the bottom of Competitive
  Research wrapping (2026-08-21).

  The trade is deliberate. A very long label in a very narrow column will now
  overflow rather than wrap — which is loud and gets fixed, where wrapping is
  quiet and ships. Labels in this row are two or three words; if one ever needs
  more, shorten the label rather than removing this.
*/

/**
 * Lev is about to do the work.
 *
 * ORANGE, not the row's green. Orange is already Lev's action colour across
 * the product — chat's action bar, the apply button on a proposed edit — so a
 * founder arriving here has been taught what it means long before they reach
 * the raise. Using the row's own accent instead would have made this row's
 * "Lev acts" signal mean nothing anywhere else, and the product's mean nothing
 * here (founder call, 2026-08-16).
 *
 * This is the general lesson worth keeping: a row is allowed its own colour
 * for CHROME, but not for a signal the founder has already learned elsewhere.
 */
export const FUNDRAISING_BTN_GENERATE =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-lev-orange px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-lev-orange-dark disabled:opacity-60";

/**
 * The founder is doing the work: save, advance, take it away.
 *
 * Accelerator surfaces (the Programs and Application blocks and lightboxes)
 * use COMMIT for Lev-work actions too (draft, retry, recalculate) on
 * purpose (T.A., 2026-10-18; LEV-2463): those screens settled on one black
 * primary per stage rather than orange-and-black side by side, and Fundraising
 * keeps GENERATE unchanged for now.
 */
export const FUNDRAISING_BTN_COMMIT =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-60";

/** A real alternative to the primary action. */
export const FUNDRAISING_BTN_SECONDARY =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60";

/**
 * SECONDARY's shape with an orange call-out: the founder already started
 * this one and should come back to it (LEV-2644, both Lev recommends and
 * All programs).
 *
 * `lev-orange-500` on the border holds 3.2:1 (the non-text/graphics floor);
 * `lev-orange-700` on the text holds 5.8:1 (the 4.5:1 text floor) — both
 * against this button's white background. Never plain Tailwind `orange`:
 * that key is a single flat value in this config (`orange: "#FFBA00"`), not
 * a scale, so `border-orange-500` etc. has no such class and renders
 * nothing.
 */
export const FUNDRAISING_BTN_OPEN_DRAFT =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-lev-orange-500 bg-white px-3.5 py-2 text-sm font-medium text-lev-orange-700 transition-colors hover:bg-lev-orange-50 disabled:opacity-60";

/** An escape or an aside. Must never compete with the primary. */
export const FUNDRAISING_BTN_QUIET =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60";

/**
 * Text treatments, and the SIZE SCALE behind them.
 *
 * An audit found the row using text-[13px] and text-[11px] against slate-400,
 * -500, -600 and -700 in nearly every combination, chosen ad hoc per panel.
 * No single instance looked wrong; together they read as the same information
 * repeated at slightly different weights, which is what makes a screen feel
 * cluttered without any one element being the problem.
 *
 * A second audit (founder call, 2026-08-16: "the font sizes are all over the
 * map") found the row rendering SIX sizes — 10, 11, 12, 13, 14 and 16px —
 * because the treatments below were only half the story: buttons picked 13
 * inline, pills picked 10, generated prose picked 14, and headers 16.
 *
 * A third pass (2026-08-17: "way too small, and not consistent with the rest
 * of the web application") found the cause of BOTH halves of that report: the
 * row had invented an 11/13px scale while the rest of the product runs on
 * Tailwind's own steps. An arbitrary-value scale cannot agree with a framework
 * scale by accident, so every screen in the raise sat one step small — and the
 * "inconsistent page by page" was the same fact from inside, with the canvas
 * panels on 11/13 and the Labs screens on 12/14.
 *
 * THREE sizes now, all Tailwind's, and each with a job:
 *
 *   text-xs    12px  hints, labels, pills — small print and metadata
 *   text-sm    14px  body, muted, error, and every button — the row's own
 *                    reading size, for controls and supporting copy
 *   text-base  16px  panel headings (StagePanelHeader), and `prose` — the
 *                    generated long-form a founder reads and pastes, which
 *                    sits on the PRODUCT's document scale (MarkdownViewer's
 *                    canvas variant), not the row's control scale
 *
 * `prose` IS a separate step, and its size is now pinned to the document
 * scale rather than left to coincidence. (Pinned to MarkdownViewer's canvas
 * body, note — not held apart from `body`. Raise `body` to 16px tomorrow and
 * the two collapse again with every test green; that would be a decision to
 * make deliberately, not drift.) It briefly collapsed into `body` when the scale moved off 13px,
 * and that collapse is what let the intro email drift a step below the raise
 * summary beside it (LEV-1496): the summary had quietly moved onto the
 * product's markdown scale, and nothing tied the two together. The rule is
 * about WHAT the text is, not which panel it sits in — controls and metadata
 * read at the row's scale, long-form the founder actually reads and pastes
 * reads at the product's document scale.
 *
 * Enforced by `design.fundraisingConsistency.test.ts`, which fails on ANY
 * arbitrary pixel value in these panels — including this file, which the first
 * sweep missed, leaving every button one step below the text beside it. If a
 * fourth step is genuinely needed, add it here and to the test — never inline,
 * or the map goes back to six.
 */
export const FUNDRAISING_TEXT = {
  /** Content the founder reads to make a decision. */
  body: "text-sm leading-relaxed text-slate-700",
  /** Supporting detail: explanations, guidance, context. */
  muted: "text-sm leading-relaxed text-slate-600",
  /** Asides, provenance, and small print. */
  hint: "text-xs leading-relaxed text-slate-500",
  /** Field and group labels. */
  label: "text-xs font-semibold text-slate-700",
  /**
   * Generated long-form the founder will paste somewhere — today only the
   * intro email body, its one call site.
   *
   * 16px, matching MarkdownViewer's canvas variant, and that match is the
   * whole point of the token (LEV-1496). Long-form reaches the screen by two
   * unrelated paths: this one, and markdown through MarkdownViewer. The raise
   * summary took the second path in 84103de61 when it grew bullets, which
   * silently moved it from 14px to 16px; the intro email stayed on 14px, and
   * the two sat one step apart, two cards from each other on the same screen,
   * through a dedicated font-scale sweep and two founder reports.
   *
   * So this is deliberately NOT `body`. It is a step larger, because it is
   * measured against the product's document scale rather than the row's
   * controls. `design.fundraisingConsistency.test.ts` pins the two together —
   * change this and that test tells you what else has to move.
   */
  prose: "text-base leading-relaxed text-neutral-900",
  /**
   * An inline failure the founder can act on — "that did not finish, try
   * again".
   *
   * Boxed rather than bare, which is the treatment the deck stage already
   * used twice. The raise summary rendered the same meaning as a bare rose
   * line, and next to a boxed one it did not read as an error at all: it read
   * as red text somebody had forgotten to style. Picking the majority spelling
   * over inventing a third is the whole point of a token.
   */
  error: "rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700",
} as const;

/**
 * The three sizes, as Tailwind classes: 12 / 14 / 16px.
 *
 * They are Tailwind's OWN steps, and that is the point. The row previously ran
 * on an invented 11/13px scale, which put every screen in the raise one step
 * smaller than the rest of the product — the founder's report was that the
 * text was "way too small and not consistent page by page or consistent with
 * the rest of the web application" (2026-08-17), and both halves of that had
 * the same cause. An arbitrary-value scale cannot agree with a framework
 * scale by accident, and nothing was reconciling them.
 *
 * Dropping the arbitrary values also removes a whole class of drift: there is
 * no `text-[12px]` to reach for when `text-xs` is right there, and the sizes
 * below the scale (9px, 10px, 11px turned up across four directories) have
 * nowhere to hide.
 *
 * Exported so the consistency test asserts against the SAME list the tokens
 * are built from. A test carrying its own copy of the scale drifts from the
 * scale the moment one of them changes, and then it enforces history.
 */
export const FUNDRAISING_TEXT_SIZES = ["text-xs", "text-sm", "text-base"] as const;

/** Every card in the row. */
export const FUNDRAISING_CARD =
  "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm";

/**
 * Every Lev tip under an Application question ("What {reviewer}s weigh" on
 * Draft, the suggested answer in Interview prep) reads from this one style,
 * so tips look the same everywhere. Neutral slate: accent colours stay with
 * the row's GENERATE/COMMIT actions.
 */
export const FUNDRAISING_TIP_BOX =
  "rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600";
/** The tip's lead-in label ("What partners weigh:", "Lev's suggested answer:"). */
export const FUNDRAISING_TIP_LABEL = "font-medium text-slate-500";

/**
 * The footer that hands off to whatever comes next.
 *
 * Consistent placement matters more than consistent styling here: the founder
 * learns that the bottom of a stage tells them where they are going, and a
 * stage that puts it somewhere else costs them a scan of the whole screen.
 */
export const FUNDRAISING_HANDOFF =
  "mt-6 flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3";

/**
 * Status pill, by meaning. Used for freshness and readiness across the row so
 * "out of date" looks the same on a deck version as on a raise summary.
 */
export const FUNDRAISING_PILL = {
  /** Something is ready, done, or on file. */
  good: "rounded px-1.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700",
  /** Something is stale, incomplete, or needs attention. */
  warn: "rounded px-1.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700",
  /** Something is weak or critical: worth fixing first. */
  bad: "rounded px-1.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700",
  /** Neutral metadata. */
  muted: "rounded px-1.5 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600",
  /** The one item that is current right now (a stage rail's current stage). */
  current: "rounded px-1.5 py-0.5 text-xs font-semibold bg-slate-900 text-white",
} as const;

/**
 * The single phrase for a stale artifact, everywhere in the row.
 *
 * Deck versions said "out of date", the raise summary said "out of date", and
 * nothing stopped the next panel saying "stale" or "needs refresh". One
 * constant means one phrase.
 */
export const FUNDRAISING_STALE_LABEL = "out of date";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * One word per idea, across every surface in the row.
 *
 * Terminology drifted three times in two days: the tracker was also called a
 * CRM and a board, block names differed between the rail and the block itself,
 * and "the row" leaked into founder copy from our own vocabulary. Each was
 * invisible while writing one panel and obvious reading four.
 *
 * `banned` is the part that earns its keep — it names the near-synonym that
 * actually showed up, so the guard fails on the specific mistake rather than
 * on a general style opinion.
 */
export const FUNDRAISING_VOCABULARY: {
  use: string;
  banned: string[];
  why: string;
}[] = [
  {
    use: "Investor Tracker",
    banned: ["CRM", "pipeline board", "deal board"],
    why: "CRM is a salesperson's word. A founder tracks investors; they do not run a CRM.",
  },
  {
    use: "warm path",
    banned: ["warm intro path", "intro route", "connection path"],
    why: "One phrase for the single most valuable line on a prospect row.",
  },
  {
    use: "out of date",
    banned: ["stale", "needs refresh", "outdated"],
    why: "Freshness is reported on decks, summaries and profiles; one phrase means one state.",
  },
  {
    use: "round",
    banned: ["raise amount", "the ask amount"],
    why: "The round is the thing being run. 'The ask' is a slide, not the round.",
  },
];
